import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { UIAwardsLayout } from './ui-awards.enum';
import { Attribute } from '../../decorators/attribute.decorator';
import { CMS_CONTENT_MODES } from '../../cms';
import styles from './ui-awards.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAwards
 * @element ui-awards
 * @classdesc Represents a class for <code>ui-awards</code> element.
 * Container for ui-award. Can have only UIAward or UISlides children.
 * @property {UIAwardsLayout} [layout="fixed"] {@attr layout} Layout for ui-awards:
 *   {@desc fixed: default layout, all ui-award elements has fixed size}
 *   {@desc stretched: all ui-award elements will fit the page in full width with fluid width}
 * @slot {@type "ui-award"}
 * @example
 * <ui-awards layout="stretched">
 *   <ui-award price="5400" label-price="Points" group-id="1" award-id="11"
 *      image="assets/mocks/images/award-mock-01.jpg"
 *   >
 *     <ui-badge color="turquoise">Business</ui-badge>
 *     <p>New image size: 600x600 px! Old ones will be scaled up.</p>
 *   </ui-award>
 *   <ui-award price="5" label-price="Points" group-id="2" award-id="22"
 *      image="assets/mocks/images/award-mock-02.jpg"
 *   >
 *     <ui-badge color="lilac">Campaign</ui-badge>
 *     <p>20% discount on the agreement fee of a business loan (of up to 100,000 euros)</p>
 *   </ui-award>
 *   <ui-award price="10" label-price="Points" group-id="3" award-id="33"
 *     image="assets/mocks/images/award-mock-03.jpg"
 *   >
 *     <ui-badge color="orange">Gold</ui-badge>
 *     <p>A 20% discount on Ampstükk raw energy bars</p>
 *   </ui-award>
 * </ui-awards>
 */
@Component({
    tag: 'ui-awards',
    styles,
    cms: {
        mode: CMS_CONTENT_MODES.Children,
        allowedChildren: ['ui-award'],
    },
})
export class UIAwards extends UIElement {
    @Attribute(String) layout: UIAwardsLayout = UIAwardsLayout.Fixed;

    /**
     * @inheritDoc
     */
    render(): void {
        const awards = this.querySelectorAll(':scope > ui-award');
        if (awards.length === 0) {
            return;
        }

        this.setAttribute('role', 'list');
        awards.forEach((award) => {
            award.setAttribute('role', 'listitem');
        });
    }
}
